import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@izicvs/ui";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { MagicWand } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useToast } from "@/client/hooks/use-toast";
import { useGenerateResume } from "@/client/services/ai/generate-resume";
import { useImportResume } from "@/client/services/resume/import";
import { useDialog } from "@/client/stores/dialog";

const MIN_LENGTH = 20;

export const AiGenerateDialog = () => {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isOpen, close } = useDialog("ai");
  const { generate, loading: generating } = useGenerateResume();
  const { importResume, loading: importing } = useImportResume();

  const [input, setInput] = useState("");
  const loading = generating || importing;

  const onGenerate = async () => {
    if (input.trim().length < MIN_LENGTH) return;
    try {
      const { data, title } = await generate({ input: input.trim(), language: i18n.locale });
      const resume = await importResume({ title, data });
      close();
      setInput("");
      void navigate(`/builder/${resume.id}`);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not generate your resume`,
        description: (error as Error).message,
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <MagicWand />
            {t`Create a resume with AI`}
          </DialogTitle>
          <DialogDescription>
            {t`Describe your background, or paste an old resume or your LinkedIn profile. The AI structures it into a resume. It never invents facts.`}
          </DialogDescription>
        </DialogHeader>

        <textarea
          autoFocus
          value={input}
          rows={9}
          disabled={loading}
          placeholder={t`e.g. I'm Marie, a frontend developer with 4 years of experience at Acme and Globex. I build React apps, led a redesign that lifted conversion by 30%, and I studied computer science at Paris-Saclay...`}
          className="w-full resize-none rounded-lg border bg-background p-3 text-sm leading-relaxed outline-none ring-primary focus:ring-2"
          onChange={(event) => {
            setInput(event.target.value);
          }}
        />

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <p className="text-xs opacity-50">
            {t`This uses your AI allowance. Pro and Autopilot get more.`}
          </p>
          <Button disabled={loading || input.trim().length < MIN_LENGTH} onClick={onGenerate}>
            <MagicWand className="mr-2" />
            {loading ? t`Generating...` : t`Generate my resume`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
